import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Bot, Send, Loader2, RefreshCw, Sparkles, Compass, AlertTriangle, Lightbulb, ArrowRight, Target, GitBranch, ChevronDown } from "lucide-react";
import { listScanChat, sendScanChat, clearScanChat, runInvestigationCopilot, type CopilotPlan } from "@/lib/intel/feeds.functions";
import { getPaddleEnvironment } from "@/lib/paddle";

type Props = {
  scanRunId: string;
  target: string;
  /** Optional handler — when provided, shows a "Broaden search" button */
  onBroaden?: () => void;
  className?: string;
  heightClass?: string;
};

export function ScanAiChat({ scanRunId, target, onBroaden, className = "", heightClass = "h-[440px]" }: Props) {
  const list = useServerFn(listScanChat);
  const send = useServerFn(sendScanChat);
  const clear = useServerFn(clearScanChat);
  const copilot = useServerFn(runInvestigationCopilot);
  const qc = useQueryClient();

  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<"chat" | "copilot" | "reasoning">("chat");
  const [openStep, setOpenStep] = useState<number | null>(null);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const plan = useMutation({
    mutationFn: () => copilot({ data: { scanRunId, environment: getPaddleEnvironment() } }) as Promise<CopilotPlan>,
  });

  const q = useQuery({
    queryKey: ["scan-chat", scanRunId],
    queryFn: () => list({ data: { scanRunId } }),
  });
  const messages = q.data ?? [];

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, busy]);

  const submit = async (override?: string) => {
    const text = (override ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setErr(null);
    setBusy(true);
    setTab("chat");
    // optimistic
    qc.setQueryData(["scan-chat", scanRunId], [
      ...messages,
      { id: `tmp-${Date.now()}`, role: "user" as const, content: text, citations: [], createdAt: new Date().toISOString() },
    ]);
    try {
      await send({ data: { scanRunId, message: text, environment: getPaddleEnvironment() } });
      await qc.invalidateQueries({ queryKey: ["scan-chat", scanRunId] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "AI request failed");
      await qc.invalidateQueries({ queryKey: ["scan-chat", scanRunId] });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!confirm("Clear this scan's AI conversation?")) return;
    await clear({ data: { scanRunId } });
    await qc.invalidateQueries({ queryKey: ["scan-chat", scanRunId] });
  };

  const runPivot = (query: string) => {
    void submit(query);
  };

  const confTone = (n: number) =>
    n >= 75 ? "text-success" : n >= 45 ? "text-amber" : "text-danger";

  return (
    <div className={`glass rounded-xl p-3 flex flex-col ${heightClass} ${className}`}>
      <div className="flex items-center gap-2 px-1 pb-2 border-b border-panel-border">
        <div className="grid h-7 w-7 place-items-center rounded-md bg-cyan/10 ring-1 ring-cyan/30">
          <Bot className="h-4 w-4 text-cyan" />
        </div>
        <div className="leading-tight">
          <div className="text-sm font-semibold">Investigation Copilot</div>
          <div className="font-mono text-[10px] text-muted-foreground truncate max-w-[180px]">
            scan · {target || scanRunId.slice(0, 8)}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            onClick={reset}
            title="Clear conversation"
            className="grid h-6 w-6 place-items-center rounded-md border border-panel-border text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
          <span className="font-mono text-[10px] text-success">● LIVE</span>
        </div>
      </div>

      <div className="flex gap-1 pt-2">
        {(["chat", "copilot", "reasoning"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-md px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${
              tab === t ? "bg-cyan/15 text-cyan ring-1 ring-cyan/30" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "chat" ? "Chat" : t === "copilot" ? "Plan" : "Reasoning"}
          </button>
        ))}
        {tab !== "chat" && (
          <button
            onClick={() => plan.mutate()}
            disabled={plan.isPending}
            className="ml-auto inline-flex items-center gap-1 rounded-md border border-cyan/40 bg-cyan/10 px-2 py-1 font-mono text-[10px] text-cyan disabled:opacity-50"
          >
            {plan.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Compass className="h-3 w-3" />}
            {plan.data ? "Re-investigate" : "Investigate"}
          </button>
        )}
      </div>

      {tab === "reasoning" && (
        <div className="flex-1 overflow-y-auto py-2 space-y-2 text-sm">
          {!plan.data && !plan.isPending && !plan.error && (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              Run an investigation pass to see the step-by-step reasoning trace: how each entity was connected,
              what evidence each inference rests on, and where the trail went dead.
            </div>
          )}
          {plan.isPending && (
            <div className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm text-muted-foreground animate-pulse">
              Reconstructing the reasoning chain…
            </div>
          )}
          {plan.error && (
            <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {plan.error instanceof Error ? plan.error.message : "Copilot failed"}
            </div>
          )}
          {plan.data && plan.data.reasoningTrace.length === 0 && (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              The copilot returned no reasoning steps for this pass. Re-run the investigation.
            </div>
          )}
          {plan.data && plan.data.reasoningTrace.length > 0 && (
            <div className="relative pl-5">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-panel-border" />
              <div className="space-y-2">
                {plan.data.reasoningTrace.map((s) => {
                  const dead = s.outcome === "dead_end";
                  const open = openStep === s.step;
                  return (
                    <div key={s.step} className="relative">
                      <div
                        className={`absolute -left-[15px] top-3 h-2.5 w-2.5 rounded-full bg-background ring-2 ${
                          dead ? "ring-amber" : s.outcome === "weak" ? "ring-muted-foreground" : "ring-cyan"
                        }`}
                      />
                      <div
                        className={`rounded-md border p-3 ${
                          dead ? "border-amber/30 bg-amber/5 opacity-90" : "border-panel-border bg-background/40"
                        }`}
                      >
                        <button
                          onClick={() => setOpenStep(open ? null : s.step)}
                          className="flex w-full items-start justify-between gap-2 text-left"
                        >
                          <div className="min-w-0">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                              Step {s.step} · {s.outcome.replace("_", " ")}
                            </div>
                            <div className="mt-0.5 font-medium break-words">{s.action || s.inference.slice(0, 80)}</div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <span className={`font-mono text-[10px] ${confTone(s.confidence)}`}>{s.confidence}%</span>
                            <ChevronDown className={`h-3 w-3 text-muted-foreground transition ${open ? "rotate-180" : ""}`} />
                          </div>
                        </button>

                        <div className="mt-1.5 h-1 w-full rounded-full bg-panel-border/60">
                          <div
                            className={`h-1 rounded-full ${s.confidence >= 75 ? "bg-success" : s.confidence >= 45 ? "bg-amber" : "bg-danger"}`}
                            style={{ width: `${s.confidence}%` }}
                          />
                        </div>

                        {open && (
                          <div className="mt-2 space-y-1.5 text-xs">
                            {s.observation && (
                              <div>
                                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Observed</span>
                                <p className="mt-0.5 break-words">{s.observation}</p>
                              </div>
                            )}
                            {s.inference && (
                              <div>
                                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Inferred</span>
                                <p className="mt-0.5 break-words">{s.inference}</p>
                              </div>
                            )}
                            {s.evidence.length > 0 && (
                              <div>
                                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Evidence</span>
                                <ul className="mt-0.5 list-disc pl-4 text-muted-foreground">
                                  {s.evidence.map((e, j) => (
                                    <li key={j} className="break-all">
                                      {/^https?:\/\//.test(e) ? (
                                        <a href={e} target="_blank" rel="noreferrer" className="text-cyan hover:underline">{e}</a>
                                      ) : e}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {s.pivot && !dead && (
                              <button
                                onClick={() => runPivot(`Follow the pivot ${s.pivot} that came out of step ${s.step} (${s.action}). Explain its link to ${target}.`)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 rounded-full border border-cyan/40 bg-cyan/10 px-2.5 py-0.5 font-mono text-[10px] text-cyan hover:bg-cyan/20 disabled:opacity-50"
                              >
                                <ArrowRight className="h-3 w-3" /> Pursue {s.pivot.slice(0, 40)}
                              </button>
                            )}
                            {dead && (
                              <div className="inline-flex items-center gap-1 font-mono text-[10px] text-amber">
                                <AlertTriangle className="h-3 w-3" /> Trail terminated here
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex items-center gap-1.5 px-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <GitBranch className="h-3 w-3" /> {plan.data.reasoningTrace.length} reasoning steps ·{" "}
                {plan.data.reasoningTrace.filter((s) => s.outcome === "dead_end").length} dead ends
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "copilot" && (
        <div className="flex-1 overflow-y-auto py-2 space-y-3 text-sm">
          {!plan.data && !plan.isPending && !plan.error && (
            <div className="px-2 py-3 text-xs text-muted-foreground">
              The copilot actively investigates this scan: it forms hypotheses, ranks the next pivots and why they matter,
              flags dead ends, and scores the confidence of every conclusion.
            </div>
          )}
          {plan.isPending && (
            <div className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm text-muted-foreground animate-pulse">
              Copilot investigating · verifying evidence, forming hypotheses…
            </div>
          )}
          {plan.error && (
            <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {plan.error instanceof Error ? plan.error.message : "Copilot failed"}
            </div>
          )}
          {plan.data && (
            <>
              <div className="rounded-md border border-panel-border bg-background/40 p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Synthesis</div>
                  <div className={`font-mono text-[10px] ${confTone(plan.data.overallConfidence)}`}>
                    confidence {plan.data.overallConfidence}%
                  </div>
                </div>
                <p className="mt-1 text-sm leading-relaxed">{plan.data.summary}</p>
              </div>

              {plan.data.hypotheses.length > 0 && (
                <section className="space-y-2">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-cyan">
                    <Lightbulb className="h-3 w-3" /> Hypotheses
                  </div>
                  {plan.data.hypotheses.map((h, i) => (
                    <div key={i} className="rounded-md border border-panel-border bg-background/40 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium">{h.statement}</div>
                        <span className={`shrink-0 font-mono text-[10px] ${confTone(h.confidence)}`}>{h.confidence}%</span>
                      </div>
                      {h.reasoning && <p className="mt-1 text-xs text-muted-foreground">{h.reasoning}</p>}
                      {h.supporting.length > 0 && (
                        <ul className="mt-1.5 list-disc pl-4 text-xs text-muted-foreground">
                          {h.supporting.map((s, j) => <li key={j} className="break-words">{s}</li>)}
                        </ul>
                      )}
                      {h.contradicting.length > 0 && (
                        <div className="mt-1.5 text-xs text-amber">
                          Contradicting: {h.contradicting.join("; ")}
                        </div>
                      )}
                      {h.tests.length > 0 && (
                        <button
                          onClick={() => runPivot(`Test this hypothesis: ${h.statement}. Run these checks: ${h.tests.join("; ")}`)}
                          className="mt-2 inline-flex items-center gap-1 rounded-full border border-cyan/40 bg-cyan/10 px-2.5 py-0.5 font-mono text-[10px] text-cyan hover:bg-cyan/20"
                        >
                          <ArrowRight className="h-3 w-3" /> Test hypothesis
                        </button>
                      )}
                    </div>
                  ))}
                </section>
              )}

              {plan.data.pivots.length > 0 && (
                <section className="space-y-2">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-cyan">
                    <Target className="h-3 w-3" /> Suggested pivots
                  </div>
                  {plan.data.pivots.map((p, i) => (
                    <div key={i} className="rounded-md border border-panel-border bg-background/40 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate font-medium">{p.target}</div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            {p.type} · {p.priority} priority
                          </div>
                        </div>
                        <span className={`shrink-0 font-mono text-[10px] ${confTone(p.confidence)}`}>{p.confidence}%</span>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">{p.why}</p>
                      <button
                        onClick={() => runPivot(p.query)}
                        disabled={busy}
                        className="mt-2 inline-flex items-center gap-1 rounded-full border border-cyan/40 bg-cyan/10 px-2.5 py-0.5 font-mono text-[10px] text-cyan hover:bg-cyan/20 disabled:opacity-50"
                      >
                        <ArrowRight className="h-3 w-3" /> Pursue pivot
                      </button>
                    </div>
                  ))}
                </section>
              )}

              {plan.data.conclusions.length > 0 && (
                <section className="space-y-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-cyan">Conclusions</div>
                  {plan.data.conclusions.map((c, i) => (
                    <div key={i} className="rounded-md border border-panel-border bg-background/40 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium">{c.statement}</div>
                        <span className={`shrink-0 font-mono text-[10px] ${confTone(c.confidence)}`}>{c.confidence}%</span>
                      </div>
                      {c.basis && <p className="mt-1 text-xs text-muted-foreground">{c.basis}</p>}
                    </div>
                  ))}
                </section>
              )}

              {plan.data.deadEnds.length > 0 && (
                <section className="space-y-2">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber">
                    <AlertTriangle className="h-3 w-3" /> Dead ends
                  </div>
                  {plan.data.deadEnds.map((d, i) => (
                    <div key={i} className="rounded-md border border-amber/30 bg-amber/5 p-3">
                      <div className="text-sm font-medium">{d.path}</div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{d.reason}</p>
                    </div>
                  ))}
                </section>
              )}

              {plan.data.citations.length > 0 && (
                <section className="space-y-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Sources</div>
                  <ul className="space-y-0.5 text-xs">
                    {plan.data.citations.map((c, i) => (
                      <li key={i} className="truncate">
                        <a href={c} target="_blank" rel="noreferrer" className="text-cyan hover:underline">{c}</a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      )}

      <div ref={scrollerRef} className={`flex-1 overflow-y-auto py-2 space-y-2 ${tab === "chat" ? "" : "hidden"}`}>
        {q.isLoading && <div className="text-xs text-muted-foreground">Loading conversation…</div>}
        {!q.isLoading && messages.length === 0 && (
          <div className="text-xs text-muted-foreground px-2 py-3">
            I actively investigate this scan — hypotheses, pivots with reasons, dead ends and confidence scores. Open
            "Investigation plan" for an autonomous pass, or ask anything here.
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
            <div
              className={`max-w-[88%] whitespace-pre-wrap rounded-md px-3 py-2 text-sm leading-relaxed ${
                m.role === "user"
                  ? "bg-cyan/15 ring-1 ring-cyan/30"
                  : "bg-background/60 border border-panel-border"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex">
            <div className="rounded-md border border-panel-border bg-background/40 px-3 py-2 text-sm text-muted-foreground animate-pulse">
              CARIO-AGENT analyzing…
            </div>
          </div>
        )}
        {err && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>
        )}
      </div>

      <div className="border-t border-panel-border pt-2 space-y-1.5">
        <div className="flex flex-wrap gap-1">
          {onBroaden && (
            <button
              onClick={onBroaden}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-full border border-cyan/40 bg-cyan/10 px-2.5 py-0.5 font-mono text-[10px] text-cyan hover:bg-cyan/20 disabled:opacity-50"
            >
              <Sparkles className="h-3 w-3" /> Broaden search
            </button>
          )}
          {["What are the next best pivots and why?", "Generate investigation hypotheses", "Which leads are dead ends?", "How confident are you and why?", "Cite the primary documents"].map((s) => (
            <button
              key={s}
              onClick={() => setInput(s)}
              className="rounded-full border border-panel-border bg-background/40 px-2.5 py-0.5 font-mono text-[10px] text-muted-foreground hover:text-foreground hover:border-cyan/40"
            >
              {s}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex items-center gap-1.5 rounded-md border border-panel-border bg-background/40 px-2 py-1.5"
        >
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask the copilot about this scan…"
            disabled={busy}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={busy || !input.trim()}
            className="grid h-7 w-7 place-items-center rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
        </form>
      </div>
    </div>
  );
}